'use client'

import { useEffect, useState, useCallback } from 'react'

const THEME_KEY = 'aden-theme'
type Theme = 'light' | 'dark'

function getSystemTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>('light')

  // Hydrate from localStorage on mount, fallback to system preference
  useEffect(() => {
    const stored = localStorage.getItem(THEME_KEY) as Theme | null
    const initial = stored ?? getSystemTheme()
    setThemeState(initial)
    applyTheme(initial)
  }, [])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    applyTheme(next)
    localStorage.setItem(THEME_KEY, next)
  }, [])

  const toggle = useCallback(() => {
    setThemeState(current => {
      const next: Theme = current === 'dark' ? 'light' : 'dark'
      applyTheme(next)
      localStorage.setItem(THEME_KEY, next)
      return next
    })
  }, [])

  return { theme, setTheme, toggle } as const
}
